// Batch Processing & Bulk Operations Service
// Netflix-style batch processing for 10-100x faster database writes

import { logger } from '../utils/logger';
import { connectionPool } from './connectionPool';
import { requestTracing } from './requestTracing';

interface BatchItem {
  table: string;
  data: any;
  operation: 'insert' | 'update' | 'upsert';
  where?: Record<string, any>; // For updates
}

class BatchProcessorService {
  private queue: BatchItem[] = [];
  private readonly BATCH_SIZE = 50; // Flush when queue reaches this size
  private readonly FLUSH_INTERVAL = 1000; // Flush every 1 second
  private flushTimer: NodeJS.Timeout | null = null;
  private isFlushing = false;

  constructor() {
    this.startPeriodicFlush();
    
    // Flush on page unload
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => {
        this.flush();
      });
    }
  }

  /**
   * Add item to batch queue
   */
  async addToBatch(
    table: string,
    data: any,
    operation: 'insert' | 'update' | 'upsert' = 'insert',
    where?: Record<string, any>
  ): Promise<void> {
    const item: BatchItem = {
      table,
      data,
      operation,
      where
    };

    this.queue.push(item);

    // Auto-flush if batch size reached
    if (this.queue.length >= this.BATCH_SIZE) {
      await this.flush();
    }
  }

  /**
   * Flush all queued items
   */
  async flush(): Promise<void> {
    if (this.isFlushing || this.queue.length === 0) {
      return;
    }

    this.isFlushing = true;
    const traceId = requestTracing.startTrace('batch_flush', {
      queueSize: this.queue.length
    });

    // Group items by table and operation
    const grouped = this.groupByTableAndOperation(this.queue);
    const itemsToFlush = this.queue.splice(0); // Clear queue

    try {

      // Process each group
      const client = connectionPool.getClient();
      const results = [];

      for (const [key, items] of Object.entries(grouped)) {
        const [table, operation] = key.split('::');
        
        try {
          if (operation === 'insert') {
            // Bulk insert
            const data = items.map(item => item.data);
            const { data: result, error } = await client
              .from(table)
              .insert(data)
              .select();
            
            if (error) {
              logger.error(`Batch insert error for ${table}:`, error);
              // Re-add failed items to queue
              items.forEach(item => this.queue.push(item));
            } else {
              results.push({ table, operation, count: result?.length || 0 });
              logger.debug(`[Batch] Inserted ${result?.length || 0} items into ${table}`);
            }
          } else if (operation === 'upsert') {
            // Bulk upsert
            const data = items.map(item => item.data);
            const { data: result, error } = await client
              .from(table)
              .upsert(data, { onConflict: 'id' })
              .select();
            
            if (error) {
              logger.error(`Batch upsert error for ${table}:`, error);
              items.forEach(item => this.queue.push(item));
            } else {
              results.push({ table, operation, count: result?.length || 0 });
              logger.debug(`[Batch] Upserted ${result?.length || 0} items into ${table}`);
            }
          } else if (operation === 'update') {
            // Batch update (process individually as Supabase doesn't support bulk update with different WHERE clauses)
            for (const item of items) {
              try {
                let query = client.from(table).update(item.data);
                
                // Apply WHERE conditions
                if (item.where) {
                  Object.entries(item.where).forEach(([key, value]) => {
                    query = query.eq(key, value);
                  });
                }
                
                const { error } = await query;
                
                if (error) {
                  logger.error(`Batch update error for ${table}:`, error);
                  this.queue.push(item); // Re-add failed item
                } else {
                  results.push({ table, operation, count: 1 });
                }
              } catch (error) {
                logger.error(`Batch update error for ${table}:`, error);
                this.queue.push(item);
              }
            }
          }
        } catch (error) {
          logger.error(`Error processing batch for ${table}:`, error);
          // Re-add failed items
          items.forEach(item => this.queue.push(item));
        }
      }

      requestTracing.endTrace(traceId, true, undefined, {
        processedItems: itemsToFlush.length,
        results: results.length,
        successCount: results.reduce((sum, r) => sum + r.count, 0)
      });

      logger.info(`[Batch] Flushed ${itemsToFlush.length} items across ${results.length} operations`);
    } catch (error: any) {
      logger.error('[Batch] Flush error:', error);
      requestTracing.endTrace(traceId, false, error.message);
      // Re-add all items to queue for retry
      this.queue.unshift(...itemsToFlush);
    } finally {
      this.isFlushing = false;
    }
  }

  /**
   * Group items by table and operation
   */
  private groupByTableAndOperation(items: BatchItem[]): Record<string, BatchItem[]> {
    const grouped: Record<string, BatchItem[]> = {};

    for (const item of items) {
      const key = `${item.table}::${item.operation}`;
      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(item);
    }

    return grouped;
  }

  /**
   * Start periodic flush
   */
  private startPeriodicFlush(): void {
    this.flushTimer = setInterval(() => {
      this.flush();
    }, this.FLUSH_INTERVAL);
  }

  /**
   * Get queue size
   */
  getQueueSize(): number {
    return this.queue.length;
  }

  /**
   * Clear queue (use with caution)
   */
  clearQueue(): void {
    this.queue = [];
    logger.warn('[Batch] Queue cleared');
  }

  /**
   * Force flush (synchronous, waits for completion)
   */
  async forceFlush(): Promise<void> {
    await this.flush();
  }
}

export const batchProcessor = new BatchProcessorService();

